<template>
  <section class="questionnaire-v2">
    <h2>职业健康调查问卷（V2）</h2>
    <p class="v2-notice">
      {{
        preview
          ? "问卷预览：可试填，不会提交或保存答案。"
          : "请完成带 * 的必填题目后提交问卷。"
      }}
    </p>
    <p class="v2-introduction">{{ introduction }}</p>
    <div
      v-for="question in visible"
      :data-question-id="question.id"
      :key="question.id"
      :ref="(el) => setRef(question.id, el)"
    >
      <h3 v-if="sections[question.id]">{{ sections[question.id] }}</h3>
      <p v-if="notes[question.id]" class="v2-note">{{ notes[question.id] }}</p>
      <div
        v-if="question.type === 'MULTIPLE_CHOICE'"
        class="v2-multiple"
        :class="{ 'v2-error': errors[question.id] }"
      >
        <p>
          <span class="v2-required">*</span>{{ number(question.id) }}.
          {{ question.title }}
        </p>
        <el-checkbox-group
          :model-value="multiValues(question)"
          @change="changeMultiple(question, $event)"
        >
          <div
            v-for="(opt, index) in question.options"
            :key="opt.value"
            class="v2-multi-option"
          >
            <el-checkbox :label="opt.value"
              >{{ String.fromCharCode(65 + index) }}.
              {{ opt.label }}</el-checkbox
            >
            <input
              v-if="
                opt.allowCustomInput &&
                multiValues(question).includes(opt.value)
              "
              :value="customValue(question, opt)"
              placeholder="请填写其他内容"
              :aria-label="question.title + '其他内容'"
              @input="changeCustom(question, opt, $event.target.value)"
            />
          </div>
        </el-checkbox-group>
      </div>
      <QuestionnaireItem
        v-else
        :question="question"
        :current-index="number(question.id)"
        :total-count="template.questions.length"
        :all-answers="answers"
        :has-error="Boolean(errors[question.id])"
        v-model="answers[question.id]"
      />
      <p v-if="errors[question.id]" class="v2-error-message" role="alert">
        {{ errors[question.id] }}
      </p>
    </div>
    <p v-if="submitError" class="v2-error-message" role="alert">
      {{ submitError }}
    </p>
    <el-button
      v-if="!preview"
      type="primary"
      :loading="submitting"
      @click="submit"
      >提交问卷</el-button
    >
    <p v-else class="v2-notice">— V2 问卷预览结束 —</p>
  </section>
</template>
<script>
import QuestionnaireItem from "./QuestionnaireItem.vue";
import { post } from "@/request.js";
import support from "@/utils/questionnaireV2.cjs";
export default {
  name: "QuestionnaireV2",
  components: { QuestionnaireItem },
  props: {
    template: { type: Object, required: true },
    examId: { type: [String, Number], default: "" },
    studentId: { type: [String, Number], default: "" },
    preview: { type: Boolean, default: false },
  },
  emits: ["submit-success"],
  data() {
    return {
      answers: {},
      errors: {},
      submitError: "",
      submitting: false,
      saveTimer: null,
      refs: {},
      introduction: support.introduction,
      sections: support.sections,
      notes: support.notes,
    };
  },
  computed: {
    visible() {
      return support.visibleQuestions(this.template.questions, this.answers);
    },
    ownerId() {
      try {
        return (
          this.studentId ||
          JSON.parse(sessionStorage.getItem("userInfo") || "{}").id ||
          ""
        );
      } catch (error) {
        return "";
      }
    },
    cacheKey() {
      return support.draftKey(
        this.examId,
        this.ownerId,
        this.template.questionnaireEffectiveRevision ||
          this.template.lastUpdateTime
      );
    },
  },
  watch: {
    answers: {
      deep: true,
      handler() {
        for (const key of Object.keys(this.errors)) {
          const q = this.visible.find((q) => q.id === key);
          if (!q || !support.questionError(q, this.answers[key]))
            delete this.errors[key];
        }
        if (this.preview || !this.ownerId) return;
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => {
          try {
            localStorage.setItem(this.cacheKey, JSON.stringify(this.answers));
          } catch (error) {
            /* storage may be unavailable */
          }
        }, 250);
      },
    },
  },
  mounted() {
    if (!this.preview && this.ownerId) {
      try {
        const saved = JSON.parse(localStorage.getItem(this.cacheKey) || "{}");
        if (saved && !Array.isArray(saved) && typeof saved === "object")
          this.answers = support.submissionAnswers(
            this.template.questions,
            saved
          );
      } catch (error) {
        /* invalid draft is ignored */
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.saveTimer);
  },
  methods: {
    number(id) {
      return Number(id.match(/q(\d+)$/)[1]);
    },
    setRef(id, el) {
      if (el) this.refs[id] = el;
      else delete this.refs[id];
    },
    multiValues(q) {
      return (this.answers[q.id] || []).map((x) => x.split("|")[0]);
    },
    customValue(q, opt) {
      return (
        (this.answers[q.id] || [])
          .find((x) => x.startsWith(opt.value + "|"))
          ?.split("|")
          .slice(1)
          .join("|") || ""
      );
    },
    changeMultiple(q, values) {
      const previous = this.answers[q.id] || [];
      const keys = previous.map((x) => x.split("|")[0]);
      const changed = values.find((x) => !keys.includes(x));
      this.answers[q.id] = support.normalizeMultiple(values, changed, previous);
    },
    changeCustom(q, opt, value) {
      this.answers[q.id] = (this.answers[q.id] || []).map((x) =>
        x.split("|")[0] === opt.value ? opt.value + "|" + value : x
      );
    },
    async submit() {
      if (this.preview || this.submitting) return;
      this.errors = {};
      for (const q of this.visible) {
        const error = support.questionError(q, this.answers[q.id]);
        if (error) this.errors[q.id] = error;
      }
      const first = Object.keys(this.errors)[0];
      if (first) {
        this.$nextTick(() =>
          this.refs[first]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
        );
        return;
      }
      this.submitting = true;
      this.submitError = "";
      try {
        const res = await post(
          `/api/my/exam/${this.examId}/questionnaire/submit`,
          {
            answers: support.submissionAnswers(
              this.template.questions,
              this.answers
            ),
          },
          { timeout: 60000 }
        );
        if (!res.success) throw new Error(res.message || "问卷提交失败");
        clearTimeout(this.saveTimer);
        try {
          localStorage.removeItem(this.cacheKey);
        } catch (error) {
          /* storage may be unavailable */
        }
        this.$emit(
          "submit-success",
          res.data?.examResult || res.examResult || res.data || res
        );
      } catch (error) {
        this.submitError = error.message || "问卷提交失败，请稍后重试";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
<style scoped lang="less">
.questionnaire-v2 {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  background: white;
  color: #222;
}
h2 {
  text-align: center;
}
h3 {
  margin-top: 28px;
  padding: 12px 0;
  border-bottom: 2px solid #1677c8;
}
.v2-introduction {
  line-height: 1.9;
  white-space: pre-wrap;
}
.v2-notice,
.v2-note {
  padding: 12px;
  line-height: 1.7;
  background: #eef6ff;
}
.v2-multiple {
  margin: 20px 0;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.v2-multi-option {
  margin: 10px 0;
}
.v2-multi-option :deep(.el-checkbox) {
  max-width: 100%;
  white-space: normal;
  height: auto;
}
.v2-multi-option :deep(.el-checkbox__label) {
  white-space: normal;
  line-height: 1.6;
}
.v2-multi-option input {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  border: 1px solid #aab4c0;
}
.v2-required,
.v2-error-message {
  color: #c0392b;
}
.v2-error {
  border-color: #c0392b;
}
</style>
